import React, { useEffect } from "react";
// UI
import CustomDialog from "../../../../../components/stateless/dialog/CustomDialog";
import DropDown from "../../../../../components/stateless/dropDown/DropDown";
import CustomButton from "../../../../../components/stateless/customButton/CustomButton";
import { PDFExport } from "@progress/kendo-react-pdf";
import HtmlRender from "../../../../../components/stateless/htmlRender/HtmlRender";
import { useRulesNavigationController } from "./RulesNavigationController";
import { Loader } from "@progress/kendo-react-indicators";
import ExpansionPanel from "../../../../../components/stateless/expansionPanel/ExpansionPanel";
import { getTitleByAgencyId } from "../../../../../features/state/StateTitle-slice";
import {
  addNotificationItem,
  removeNotificationItem,
} from "../../../../../features/state/StateNotification-slice";
import "../../Rules.scss";
import { Icon } from "@progress/kendo-react-common";
import { RecordStatuses } from "../../../../../utilities/enums/RecordStatuses";
import {
  DatePicker,
  DatePickerChangeEvent,
} from "@progress/kendo-react-dateinputs";
import moment from "moment";
import ChapterHistoryTable from "./chapterHistoryTable/ChapterHistoryTable";

interface IRuleNavigation {
  agencyId?: number | null | undefined;
  titleId?: number | null | undefined;
}

const RuleNavigation = (props: IRuleNavigation) => {
  const { agencyId, titleId } = props;

  const {
    // functions
    handleTitleSelectChange,
    handleAgencySelectChange,
    triggerTitleListUpdate,
    toggleTitleDialog,
    toggleChapterDialog,
    toggleHistoryDialog,
    exportPDF,
    setDefaultValues,
    downloadPdf,
    toggleEmergencyRegulation,
    toggleRenew,
    handleRenewDateChange,
    confirmRenewDate,
    toggleSubConfirm,
    toggleParentSubscriptionConfirm,
    subExists,
    unsubscribeParentSubscribeChild,
    unsubscribeChildrenSubscribeParent,
    subscribeToAgency,
    subscribeToTitle,
    subscribeToChapter,
    recreateChapterPdf,
    recreateTitlePdf,
    // redux - these are only here because of the useEffect
    getAllAgencies,
    // state
    titleList,
    chapterValues,
    agencyTitleValues,
    agencyValues,
    dispatch,
    selectedAgency,
    selectedTitle,
    selectedChapter,
    chapterDialogOpen,
    historyDialogOpen,
    chaptersLoading,
    titleDialogOpen,
    titleData,
    pdfExport,
    isAppUser,
    emergencyRegulation,
    emergencyRegulationOpen,
    renew,
    renewed,
    renewDate,
    subLoggedIn,
    isSubConfirmOpen,
    isParentSubscriptionOpen,
    confirmDialogInfo,
    generatingTitle,
    listOfChildrenSubscriptionsForUserNotification,
    recreatingChapters,
    recreatingTitle,
    isSosUser,
  } = useRulesNavigationController(agencyId, titleId);
  //#endregion

  useEffect(() => {
    console.log("agencyTitleValues updated:", agencyTitleValues);
    console.log("chapterValues updated:", chapterValues);
  }, [agencyTitleValues, chapterValues]);

  //#region useState
  useEffect(() => {
    if (agencyValues.length < 1) {
      dispatch(getAllAgencies());
    }
    if (agencyId && titleId && !selectedAgency && !selectedTitle) {
      if (titleList.length < 1) {
        dispatch(getTitleByAgencyId(agencyId));
      } else {
        setDefaultValues();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, titleList, selectedTitle, agencyValues, getAllAgencies]);
  //#endregion

  return (
    <>
      <div className="rule-dropdowns">
        {/* View full code modal */}
        {titleDialogOpen ? (
          <CustomDialog
            size="large"
            open={titleDialogOpen}
            title={`Title ${selectedTitle.titleNumber} -  ${selectedTitle.titleName}`}
            toggleDialog={() => toggleTitleDialog()}
            hideButtons
          >
            <div className="download-pdf-button">
              <CustomButton
                disabled={chaptersLoading}
                onClick={exportPDF}
                icon="pdf"
                themeColor="success"
                size="small"
              >
                Download PDF
              </CustomButton>
            </div>
            <PDFExport
              ref={pdfExport}
              margin={40}
              fileName={`Title-${new Date().toLocaleDateString()}`}
              author="NE-Rules"
              paperSize="A4"
              scale={0.6}
            >
              {titleData.length > 0 ? (
                titleData.map((chapter: any, index: number) => {
                  return (
                    <HtmlRender
                      key={index + chapter.id}
                      value={chapter.chapterHtml}
                    />
                  );
                })
              ) : (
                <div className="loading-search">
                  <div>Loading Title </div>
                  <Loader type="infinite-spinner" />
                </div>
              )}
            </PDFExport>
          </CustomDialog>
        ) : null}

        <CustomDialog
          open={emergencyRegulationOpen}
          title="Emergency Regulation"
          toggleDialog={toggleEmergencyRegulation}
          hideButtons
          size="large"
        >
          {emergencyRegulation && (
            <div style={{ margin: "20px" }}>
              <HtmlRender
                value={emergencyRegulation.regulationHtml}
                className=""
              />
              <div>
                <DatePicker
                  onChange={(date: DatePickerChangeEvent) => {
                    handleRenewDateChange(date.value);
                  }}
                  label={"Effective To"}
                  size="medium"
                  min={moment(new Date(emergencyRegulation.effectiveTo))
                    .add(1, "days")
                    .toDate()}
                  max={moment(new Date(emergencyRegulation.effectiveTo))
                    .add(90, "days")
                    .toDate()}
                  disabled={
                    !renew || renewed || emergencyRegulation.renewCount > 0
                  }
                  defaultValue={new Date(emergencyRegulation.effectiveTo)}
                />
                {isAppUser && (
                  <div>
                    {!renew ? (
                      <CustomButton
                        onClick={toggleRenew}
                        disabled={emergencyRegulation.renewCount > 0}
                      >
                        Renew
                      </CustomButton>
                    ) : (
                      <CustomButton
                        onClick={confirmRenewDate}
                        disabled={renewed || !renewDate || renewDate === null}
                      >
                        Set New Effective To Date
                      </CustomButton>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </CustomDialog>

        {/* Download chapter modal*/}
        {selectedChapter !== null && chapterDialogOpen ? (
          <CustomDialog
            size="large"
            open={chapterDialogOpen}
            title={`Chapter ${selectedChapter.chapterNumber}: ${selectedChapter.chapterName ?? ""}`}
            toggleDialog={() => toggleChapterDialog()}
            hideButtons
          >
            <div className="download-pdf-button">
              <CustomButton
                disabled={chaptersLoading}
                onClick={exportPDF}
                icon="pdf"
                themeColor="success"
                size="small"
              >
                Download PDF
              </CustomButton>
            </div>
            <PDFExport
              ref={pdfExport}
              margin={40}
              fileName={`Chapter-${selectedChapter.chapterNumber}-${new Date().toLocaleDateString()}`}
              author="NE-Rules"
              paperSize="A4"
              scale={0.6}
            >
              <HtmlRender value={selectedChapter.chapterHtml} />
            </PDFExport>
          </CustomDialog>
        ) : null}

        {selectedChapter && selectedChapter !== null && (
          <CustomDialog
            size="large"
            open={historyDialogOpen}
            title={`Chapter ${selectedChapter.chapterNumber}: ${selectedChapter.chapterName ?? ""} History`}
            toggleDialog={() => toggleHistoryDialog()}
            hideButtons
          >
            <ChapterHistoryTable selectedChapter={selectedChapter} />
          </CustomDialog>
        )}

        <DropDown
          size="fullwidth"
          label={<strong>AGENCY</strong>}
          value={selectedAgency}
          listItems={agencyValues}
          onSelect={handleAgencySelectChange}
          onBlur={triggerTitleListUpdate}
          dataKey="id"
          textField="agencyName"
          listSize="large"
        />

        <DropDown
          size="fullwidth"
          label={<strong>TITLE</strong>}
          value={selectedTitle}
          listItems={titleList}
          onSelect={handleTitleSelectChange}
          dataKey="id"
          idName="titleNumber"
          textField="titleName"
          listSize="large"
        />
      </div>

      {subLoggedIn && (
        <div className="rule-dropdowns">
          <div>
            {selectedAgency && (
              <CustomButton
                onClick={() => {
                  subscribeToAgency(selectedAgency.id);
                }}
                themeColor="success"
                size="small"
                disabled={subExists(selectedAgency.id, "agency")}
              >
                {subExists(selectedAgency.id, "agency") ? (
                  <>
                    <Icon name="checkmark" style={{ marginRight: "3px" }} />
                    Subscribed to Agency
                  </>
                ) : (
                  <>Subscribe to Agency</>
                )}
              </CustomButton>
            )}
          </div>
          <div>
            {selectedTitle && (
              <CustomButton
                onClick={() => {
                  subscribeToTitle(selectedTitle.id);
                }}
                themeColor="success"
                size="small"
                disabled={subExists(selectedTitle.id, "title")}
              >
                {subExists(selectedTitle.id, "title") ? (
                  <>
                    <Icon name="checkmark" style={{ marginRight: "3px" }} />
                    Subscribed to Title
                  </>
                ) : (
                  <>Subscribe to Title</>
                )}
              </CustomButton>
            )}
          </div>
        </div>
      )}

      <br />
      {/*Display Chapters in Expansion Panel*/}

      {selectedTitle ? (
        <div className="chapter-results">
          <div className="pdf-heading">
            <h2>
              <strong>CHAPTERS</strong>
            </h2>
            <CustomButton
              disabled={chaptersLoading || generatingTitle}
              onClick={() => {
                downloadPdf(
                  "title-pdfs",
                  `Title_${selectedTitle.titleNumber}.pdf`,
                  selectedTitle.id,
                );
              }}
              themeColor="success"
              size="small"
              icon="pdf"
            >
              Download Title
              {generatingTitle && (
                <Loader
                  type="converging-spinner"
                  size="small"
                  themeColor="light"
                />
              )}
            </CustomButton>
            {isSosUser && (
              <CustomButton
                onClick={() => {
                  recreateTitlePdf(selectedTitle.id);
                }}
                themeColor="success"
                size="small"
                disabled={recreatingTitle}
              >
                Recreate Title Pdf
                {recreatingTitle && (
                  <Loader
                    type="converging-spinner"
                    size="small"
                    themeColor="light"
                  />
                )}
              </CustomButton>
            )}
            <CustomButton
              disabled={chaptersLoading}
              onClick={() => {
                navigator.clipboard.writeText(
                  `${window.location.origin}/rules?agencyId=${selectedAgency.id}&titleId=${selectedTitle.id}`,
                );
                dispatch(
                  addNotificationItem({
                    message: "URL copied to clipboard",
                    type: "success",
                  }),
                );
                setTimeout(() => {
                  dispatch(removeNotificationItem());
                }, 5000);
              }}
              icon="copy"
              size="small"
            >
              Copy Title Link
            </CustomButton>
          </div>
          {chaptersLoading ? (
            <>
              Loading <Loader type="converging-spinner" />
            </>
          ) : (
            chapterValues.map((chapter: any, index: number) => {
              console.log("Rendering chapter:", chapter);
              console.log("chapter:", chapter);
              let effectiveDate =
                chapter.effectiveDate && new Date(chapter.effectiveDate);
              return (
                <>
                  {agencyTitleValues[`${chapter.id}`]
                    ? agencyTitleValues[`${chapter.id}`].map((chapter: any) => (
                        <ExpansionPanel
                          key={chapter.id}
                          item={chapter}
                          title={
                            <div style={{ display: "flex" }}>
                              <div style={{ flexDirection: "column" }}>
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "flex-end",
                                  }}
                                >
                                  <div>
                                    {chapter.chapterName !== null
                                      ? `${chapter.chapterNumber}: ${chapter.chapterName}`
                                      : chapter.chapterNumber}
                                  </div>
                                  {chapter.emergencyRegulationId && (
                                    <div style={{ marginLeft: "5px" }}>
                                      <CustomButton
                                        onClick={() => {
                                          toggleEmergencyRegulation(
                                            chapter.emergencyRegulationId,
                                          );
                                        }}
                                        themeColor="error"
                                        icon="add"
                                        size="small"
                                      />
                                    </div>
                                  )}
                                </div>
                                {effectiveDate && (
                                  <div
                                    style={{
                                      color: "#595959",
                                      fontStyle: "italic",
                                      fontSize: "10pt",
                                    }}
                                  >
                                    {`Effective: ${effectiveDate.getMonth() + 1}/${effectiveDate.getDate()}/${effectiveDate.getFullYear()}`}
                                  </div>
                                )}
                                {chapter.recordStatusId ===
                                  RecordStatuses.Repealed && (
                                  <div
                                    style={{
                                      color: "red",
                                      fontStyle: "italic",
                                      fontSize: "10pt",
                                    }}
                                  >
                                    Repealed
                                  </div>
                                )}
                              </div>
                            </div>
                          }
                          fullwidth
                        >
                          {isAppUser && (
                            <div>
                              {chapter.certificateOfAdoptionBlobName && (
                                <CustomButton
                                  onClick={() => {
                                    downloadPdf(
                                      "certificates-of-adoption",
                                      chapter.certificateOfAdoptionBlobName ??
                                        "",
                                    );
                                  }}
                                  themeColor="success"
                                  size="small"
                                  icon="pdf"
                                >
                                  Certificate of Adoption
                                </CustomButton>
                              )}
                              {chapter.pdfContainerName &&
                                chapter.officialPdfBlobName && (
                                  <CustomButton
                                    onClick={() => {
                                      downloadPdf(
                                        chapter.pdfContainerName ??
                                          "chapter-pdfs",
                                        chapter.officialPdfBlobName ?? "",
                                        undefined,
                                        chapter.id,
                                      );
                                    }}
                                    themeColor="success"
                                    size="small"
                                    icon="pdf"
                                  >
                                    Download Official Chapter
                                  </CustomButton>
                                )}
                            </div>
                          )}
                          {isSosUser && (
                            <CustomButton
                              onClick={() => {
                                recreateChapterPdf(chapter.id);
                              }}
                              themeColor="success"
                              size="small"
                              disabled={
                                !!recreatingChapters &&
                                recreatingChapters.includes(chapter.id)
                              }
                            >
                              Recreate Chapter Pdf
                              {!!recreatingChapters &&
                                recreatingChapters.includes(
                                  chapter.id,
                                ) && (
                                  <Loader
                                    type="converging-spinner"
                                    size="small"
                                    themeColor="light"
                                  />
                                )}
                            </CustomButton>
                          )}
                          {(isAppUser ||
                            chapter.recordStatusId !==
                              RecordStatuses.Repealed) && (
                            <CustomButton
                              onClick={() => {
                                downloadPdf(
                                  chapter.pdfContainerName ?? "chapter-pdfs",
                                  chapter.pdfBlobName ?? "",
                                  undefined,
                                  chapter.id,
                                );
                              }}
                              themeColor="success"
                              size="small"
                              icon="pdf"
                            >
                              Download Chapter
                            </CustomButton>
                          )}
                          <CustomButton
                            onClick={() => toggleHistoryDialog(chapter)}
                            themeColor="success"
                            size="small"
                          >
                            View History
                          </CustomButton>
                          {chapter.recordStatusId !== RecordStatuses.Repealed &&
                            subLoggedIn && (
                              <CustomButton
                                onClick={() => {
                                  subscribeToChapter(chapter.id);
                                }}
                                themeColor="success"
                                size="small"
                                disabled={subExists(chapter.id, "chapter")}
                              >
                                {subExists(chapter.id, "chapter") ? (
                                  <>
                                    <Icon
                                      name="checkmark"
                                      style={{ marginRight: "3px" }}
                                    />
                                    Subscribed to Chapter
                                  </>
                                ) : (
                                  <>Subscribe to Chapter</>
                                )}
                              </CustomButton>
                            )}
                          <PDFExport
                            ref={pdfExport}
                            margin={"1cm"}
                            fileName={`Title-${new Date().toLocaleDateString()}`}
                            author="NE-Rules"
                            paperSize="A4"
                            scale={0.6}
                          >
                            <HtmlRender
                              value={chapter.chapterHtml ?? ""}
                              className="chapter-expansion-display"
                            />
                          </PDFExport>
                        </ExpansionPanel>
                      ))
                    : null}
                </>
              );
            })
          )}
        </div>
      ) : null}

      {/*Unsubscribe Parent Subscription*/}
      {confirmDialogInfo?.newSubId && confirmDialogInfo.newSubType && (
        <CustomDialog
          open={isSubConfirmOpen}
          title="Subscription Confirmation"
          toggleDialog={toggleSubConfirm}
          yesNo
          approveAction={unsubscribeParentSubscribeChild}
          cancelAction={toggleSubConfirm}
          size="medium"
        >
          <p>
            You are already subscribed to this through the
            {confirmDialogInfo.parentSubType} {confirmDialogInfo.parentSubName}.
            If you complete this subscription, you will remove your subscription
            to {confirmDialogInfo.parentSubName} and only be subscribed to this.
            Are you sure you want to complete this subscription?
          </p>
        </CustomDialog>
      )}

      {/*Unsubscribe Child Subscriptions*/}
      <CustomDialog
        open={isParentSubscriptionOpen}
        title="Subscription Confirmation"
        toggleDialog={toggleParentSubscriptionConfirm}
        yesNo
        approveAction={unsubscribeChildrenSubscribeParent}
        cancelAction={toggleParentSubscriptionConfirm}
        size="medium"
      >
        <p>The subscription you are subscribing to has child subscriptions. </p>
        <p>
          If you complete this subscription, you will unsubscribe to these
          Subscriptions:
        </p>
        <ul>
          {listOfChildrenSubscriptionsForUserNotification.map(
            (childSubscription: any) => {
              return (
                <li>
                  {childSubscription.type.toUpperCase()} {childSubscription.id}{" "}
                  {childSubscription.name}
                </li>
              );
            },
          )}
        </ul>

        <p>Do you wish to continue?</p>
      </CustomDialog>
    </>
  );
};

export default RuleNavigation;
